/**
 * Search Filters - Modern chip-based filter interface
 */
class SearchFilters {
    // Configuration constants
    static VIEWPORT_EDGE_BUFFER_PX = 20;
    static MOBILE_BREAKPOINT_PX = 640; // Tailwind's sm breakpoint
    static DROPDOWN_OFFSET_PX = 5;

    constructor() {
        this.form = document.querySelector('.search-form');
        if (!this.form) {
            return;
        }

        // Read user-specific age defaults from form data attributes
        this.defaultMinAge = parseInt(this.form.dataset.defaultMinAge, 10) || 13;
        this.defaultMaxAge = parseInt(this.form.dataset.defaultMaxAge, 10) || 110;
        this.sliderMax = parseInt(this.form.dataset.sliderMax, 10) || 65;

        // First, set up the outside click handler to ensure it's attached before other handlers
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
        document.addEventListener('click', this.boundHandleOutsideClick);

        // Then initialize all the UI components
        this.setupExpandableChips();
        this.setupToggleChips();
        this.setupApplyButtons();
        this.setupClearAllButton();
        this.setupClearFilterListeners();
        this.initializeSelects();
        this.setupGenericSelector();
        this.setupSortChips();
        this.setupSaveFiltersChip();
        this.setupRangeSliders();
    }
    
    /**
     * Setup expandable filter chips
     */
    setupExpandableChips() {
        const expandableChips = document.querySelectorAll('.expandable-filter-chip');
        
        expandableChips.forEach(chip => {
            const container = chip.closest('.filter-chip-container');
            if (!container) {
                // Skip if this is handled by a different setup (e.g., sort chips)
                return;
            }
            const dropdown = container.querySelector('.filter-chip-dropdown');
            
            // Prevent all change events from within this dropdown from bubbling up to the form
            if (dropdown) {
                dropdown.addEventListener('change', (e) => {
                    e.stopPropagation();
                }, false);
                
            }
            
            chip.addEventListener('click', () => {
                if (!container) {
                    // Skip if this is handled by a different setup (e.g., sort chips)
                    return;
                }
                const chevron = chip.querySelector('.fa-chevron-down');

                // Toggle dropdown visibility
                if (getComputedStyle(dropdown).display === 'none') {
                    // Close all other dropdowns first
                    document.querySelectorAll('.filter-chip-dropdown').forEach(d => {
                        if (d !== dropdown && getComputedStyle(d).display !== 'none') {
                            d.style.display = 'none';
                            const c = d.closest('.filter-chip-container').querySelector('.expandable-filter-chip');
                            if (c) {
                                c.setAttribute('aria-expanded', 'false');
                                const chevronIcon = c.querySelector('.fa-chevron-down');
                                if (chevronIcon) chevronIcon.classList.remove('rotate-180');
                            }
                        }
                    });

                    // Position the dropdown to avoid edge cutoff
                    this.positionDropdown(dropdown, container);

                    // Sync radius select with URL value when opening location dropdown
                    if (dropdown.querySelector('#radius_input')) {
                        this.syncRadiusSelectWithURL();
                    }

                    dropdown.style.display = 'block';
                    chip.setAttribute('aria-expanded', 'true');
                    if (chevron) chevron.classList.add('rotate-180');
                } else {
                    dropdown.style.display = 'none';
                    chip.setAttribute('aria-expanded', 'false');
                    if (chevron) chevron.classList.remove('rotate-180');
                }
            });
        });
    }
    
    /**
     * Position dropdown to avoid edge cutoff
     * @param {HTMLElement} dropdown - The dropdown element
     * @param {HTMLElement} container - The container element
     */
    positionDropdown(dropdown, container) {
        // Reset positioning first
        dropdown.style.right = '';
        dropdown.style.left = '';
        dropdown.style.position = '';
        dropdown.style.top = '';
        dropdown.style.maxWidth = '';
        dropdown.style.width = '';
        dropdown.classList.remove('right-aligned');

        // Get container position and viewport width
        const containerRect = container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const edgeBuffer = SearchFilters.VIEWPORT_EDGE_BUFFER_PX;
        const mobileBreakpoint = SearchFilters.MOBILE_BREAKPOINT_PX;
        const dropdownOffset = SearchFilters.DROPDOWN_OFFSET_PX;

        // Temporarily show dropdown to get accurate width
        const originalDisplay = dropdown.style.display;
        dropdown.style.display = 'block';
        dropdown.style.visibility = 'hidden';
        const dropdownWidth = dropdown.offsetWidth;
        dropdown.style.display = originalDisplay;
        dropdown.style.visibility = '';

        // Calculate available width
        const availableWidth = viewportWidth - (2 * edgeBuffer);

        // Check if we're on mobile screen
        const isMobileScreen = viewportWidth < mobileBreakpoint;

        if (isMobileScreen) {
            // Full-width on mobile screens
            dropdown.style.position = 'fixed';
            dropdown.style.left = `${edgeBuffer}px`;
            dropdown.style.right = `${edgeBuffer}px`;
            dropdown.style.top = `${containerRect.bottom + dropdownOffset}px`;
            dropdown.style.width = 'auto';
            dropdown.classList.add('right-aligned');
        } else {
            // Desktop: check if dropdown would overflow right edge
            const willOverflowRight = containerRect.left + dropdownWidth > viewportWidth - edgeBuffer;

            if (willOverflowRight) {
                // Use fixed positioning for edge cases
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${containerRect.bottom}px`;

                // If dropdown is wider than available space, constrain it
                if (dropdownWidth > availableWidth) {
                    dropdown.style.maxWidth = `${availableWidth}px`;
                    dropdown.style.left = `${edgeBuffer}px`;
                } else {
                    // Position so right edge is within viewport
                    const rightEdgeLimit = viewportWidth - edgeBuffer;
                    const calculatedLeft = rightEdgeLimit - dropdownWidth;
                    const finalLeft = Math.max(edgeBuffer, calculatedLeft);
                    dropdown.style.left = `${finalLeft}px`;
                }

                dropdown.classList.add('right-aligned');
            } else {
                // Standard positioning - align with left edge of button
                dropdown.style.position = 'absolute';
                dropdown.style.left = '0';
                dropdown.style.top = '100%';
            }
        }

        // Check if dropdown would extend beyond bottom edge and adjust if needed
        setTimeout(() => {
            const dropdownRect = dropdown.getBoundingClientRect();
            if (dropdownRect.bottom > viewportHeight - edgeBuffer) {
                const maxHeight = viewportHeight - dropdownRect.top - edgeBuffer;
                dropdown.style.maxHeight = `${maxHeight}px`;
                dropdown.style.overflowY = 'auto';
            }
        }, 0);
    }
    
    /**
     * Sync radius select with current URL value
     */
    syncRadiusSelectWithURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const radiusValue = urlParams.get('radius') || '50';
        const radiusSelect = document.getElementById('radius_input');
        if (radiusSelect) radiusSelect.value = radiusValue;
    }
    
    /**
     * Setup toggle filter chips (checkboxes)
     */
    setupToggleChips() {
        const toggleChips = document.querySelectorAll('.toggle-filter-chip');
        
        toggleChips.forEach(chip => {
            const checkbox = chip.querySelector('input[type="checkbox"]');
            
            chip.addEventListener('click', () => {
                checkbox.checked = !checkbox.checked;
                
                // Update visual state
                if (checkbox.checked) {
                    chip.classList.add('bg-sky-100', 'border-sky-300', 'text-sky-700');
                    chip.classList.remove('bg-white', 'border-gray-300');
                    
                    const icon = chip.querySelector('i');
                    if (icon) {
                        icon.classList.add('text-sky-500');
                        icon.classList.remove('text-gray-500');
                    }
                } else {
                    chip.classList.remove('bg-sky-100', 'border-sky-300', 'text-sky-700');
                    chip.classList.add('bg-white', 'border-gray-300');
                    
                    const icon = chip.querySelector('i');
                    if (icon) {
                        icon.classList.remove('text-sky-500');
                        icon.classList.add('text-gray-500');
                    }
                }
                
                this.submitForm();
            });
        });
    }

    /**
     * Listen for clear-filter events dispatched from chip × buttons
     */
    setupClearFilterListeners() {
        document.addEventListener('clear-filter', (e) => {
            const filterType = e.detail?.filterType;
            if (filterType) {
                this.clearFilterByType(filterType);
                this.submitForm();
            }
        });
    }

    /**
     * Clear all filters for a specific filter group
     */
    clearFilterByType(filterType) {
        if (filterType === 'age-gender-filter') {
            // Reset age to user-specific defaults
            const age1Input = this.form.querySelector('input[name="age1"]');
            const age2Input = this.form.querySelector('input[name="age2"]');
            if (age1Input) age1Input.value = String(this.defaultMinAge);
            if (age2Input) age2Input.value = String(this.defaultMaxAge);
            const slider = $("#age1_age2_slider");
            if (slider.length && typeof slider.slider === 'function') {
                slider.slider("values", [this.defaultMinAge, this.sliderMax]);
            }
            // Reset gender chips and disable hidden inputs directly (x-effect is async)
            const genderChips = this.form.querySelector('#age-gender-filter-dropdown .gender-chips');
            if (genderChips && genderChips._x_dataStack) {
                genderChips._x_dataStack[0].male = false;
                genderChips._x_dataStack[0].female = false;
            }
            const maleInput = document.getElementById('sex-male-input');
            const femaleInput = document.getElementById('sex-female-input');
            if (maleInput) maleInput.disabled = true;
            if (femaleInput) femaleInput.disabled = true;
        } else if (filterType === 'location-filter') {
            // Clear country hidden inputs directly (Alpine DOM update is async)
            this.form.querySelectorAll('input[name="countries[]"]').forEach(el => el.remove());
            const locationCountryEl = this.form.querySelector('[x-data*="countryChips"][x-data*="countries"]');
            if (locationCountryEl && locationCountryEl._x_dataStack) {
                locationCountryEl._x_dataStack[0].countries = [];
            }
            // Clear city
            const cityFilterEl = this.form.querySelector('[x-data*="cityFilter"]');
            if (cityFilterEl && cityFilterEl._x_dataStack) {
                cityFilterEl._x_dataStack[0].clearCity();
            }
            const cityInput = this.form.querySelector('input[name="city"]');
            const cityNameInput = this.form.querySelector('input[name="cityName"]');
            if (cityInput) cityInput.value = '';
            if (cityNameInput) cityNameInput.value = '';
            // Reset radius
            this.resetRadiusSelect('50');
            // Uncheck continent checkbox chips (Alpine-driven)
            this.resetCheckboxChips('input[name="continents[]"]');
        } else if (filterType === 'languages-filter') {
            // Clear Alpine-based language chips
            this.form.querySelectorAll('input[name="languages[]"]').forEach(el => el.remove());
            this.form.querySelectorAll('input[name="learn[]"]').forEach(el => el.remove());
            const speaksEl = this.form.querySelector('[data-lang-type="speaks"]');
            if (speaksEl && speaksEl._x_dataStack) {
                speaksEl._x_dataStack[0].languages = [];
            }
            const learnsEl = this.form.querySelector('[data-lang-type="learns"]');
            if (learnsEl && learnsEl._x_dataStack) {
                learnsEl._x_dataStack[0].languages = [];
            }
            // Uncheck option checkbox chips (Alpine-driven)
            this.resetCheckboxChips('input[name="native_only"]');
            this.resetCheckboxChips('input[name="learning_known"]');
        }
    }

    /**
     * Reset Alpine-driven checkbox chips by selector.
     * Sets the Alpine `on` state to false for each matching checkbox's parent chip.
     */
    resetCheckboxChips(selector) {
        this.form.querySelectorAll(selector).forEach(cb => {
            const chip = cb.closest('.filter-checkbox-chip');
            if (chip && chip._x_dataStack) {
                chip._x_dataStack[0].on = false;
            }
            cb.checked = false;
        });
    }

    /**
     * Setup apply buttons in filter dropdowns
     */
    setupApplyButtons() {
        const applyButtons = document.querySelectorAll('.apply-filter');

        applyButtons.forEach(button => {
            button.addEventListener('click', () => {

                // Close the dropdown
                const dropdown = button.closest('.filter-chip-dropdown');
                if (dropdown) {
                    dropdown.style.display = 'none';

                    // Reset chevron
                    const chevron = dropdown.parentElement.querySelector('.fa-chevron-down');
                    if (chevron) chevron.classList.remove('rotate-180');
                }

                // Submit the form to update results
                this.submitForm();
            });
        });
    }

    /**
     * Setup clear all button
     */
    setupClearAllButton() {
        const clearAllButton = document.getElementById('clear-all-filters');
        if (!clearAllButton) {
            return;
        }
        
        
        clearAllButton.addEventListener('click', () => {

            // Reset age - check both select elements and hidden inputs
            const age1Select = this.form.querySelector('select[name="age1"]');
            const age2Select = this.form.querySelector('select[name="age2"]');
            const age1Input = this.form.querySelector('input[name="age1"]');
            const age2Input = this.form.querySelector('input[name="age2"]');

            if (age1Select) age1Select.value = String(this.defaultMinAge);
            if (age2Select) age2Select.value = String(this.defaultMaxAge);
            if (age1Input) age1Input.value = String(this.defaultMinAge);
            if (age2Input) age2Input.value = String(this.defaultMaxAge);

            // Also update the jQuery UI slider if it exists
            const slider = $("#age1_age2_slider");
            if (slider.length && typeof slider.slider === 'function') {
                slider.slider("values", [this.defaultMinAge, this.sliderMax]);
            }
            
            // Reset gender chips and disable hidden inputs directly (x-effect is async)
            const genderChips = this.form.querySelector('#age-gender-filter-dropdown .gender-chips');
            if (genderChips && genderChips._x_dataStack) {
                genderChips._x_dataStack[0].male = false;
                genderChips._x_dataStack[0].female = false;
            }
            const maleInput = document.getElementById('sex-male-input');
            const femaleInput = document.getElementById('sex-female-input');
            if (maleInput) maleInput.disabled = true;
            if (femaleInput) femaleInput.disabled = true;

            // Clear quick filter chips (Online, New, Nearby)
            const onlineChip = this.form.querySelector('input[name="online"]')?.closest('[x-data]');
            if (onlineChip?._x_dataStack) {
                onlineChip._x_dataStack[0].on = false;
            }
            const newChip = this.form.querySelector('input[name="new"]')?.closest('[x-data]');
            if (newChip?._x_dataStack) {
                newChip._x_dataStack[0].on = false;
            }
            this.clearNearbyChip();

            // Clear country hidden inputs directly (Alpine DOM update is async)
            this.form.querySelectorAll('input[name="countries[]"]').forEach(el => el.remove());
            this.form.querySelectorAll('input[name="home_countries[]"]').forEach(el => el.remove());
            const countryChipEls = this.form.querySelectorAll('[x-data*="countryChips"]');
            countryChipEls.forEach(el => {
                if (el._x_dataStack) {
                    el._x_dataStack[0].countries = [];
                }
            });

            // Clear city
            const cityFilterEl = this.form.querySelector('[x-data*="cityFilter"]');
            if (cityFilterEl && cityFilterEl._x_dataStack) {
                cityFilterEl._x_dataStack[0].clearCity();
            }
            const cityInput = this.form.querySelector('input[name="city"]');
            const cityNameInput = this.form.querySelector('input[name="cityName"]');
            if (cityInput) cityInput.value = '';
            if (cityNameInput) cityNameInput.value = '';

            // Reset radius
            this.resetRadiusSelect('50');
            
            // Uncheck continents
            const continentCheckboxes = this.form.querySelectorAll('input[name="continents[]"]');
            continentCheckboxes.forEach(checkbox => { checkbox.checked = false; });
            
            // Clear Alpine-based language hidden inputs (async DOM)
            this.form.querySelectorAll('input[name="languages[]"]').forEach(el => el.remove());
            this.form.querySelectorAll('input[name="learn[]"]').forEach(el => el.remove());
            const speaksEl = this.form.querySelector('[data-lang-type="speaks"]');
            if (speaksEl && speaksEl._x_dataStack) {
                speaksEl._x_dataStack[0].languages = [];
            }
            const learnsEl = this.form.querySelector('[data-lang-type="learns"]');
            if (learnsEl && learnsEl._x_dataStack) {
                learnsEl._x_dataStack[0].languages = [];
            }

            // Clear languages (legacy select)
            const langSelect = this.form.querySelector('select[name="languages[]"]');
            if (langSelect) {
                for (const option of langSelect.options) {
                    option.selected = false;
                }
            }
            
            // Clear generic language selector
            const languageSelector = this.form.querySelector('#languages-selector');
            if (languageSelector) {
                // Remove all selected languages
                const selectedLanguages = languageSelector.querySelectorAll('.selected-item-chip');
                selectedLanguages.forEach(element => element.remove());
                
                // Make all options visible again
                const languageOptions = languageSelector.querySelectorAll('.option-item');
                languageOptions.forEach(option => {
                    option.style.display = '';
                    option.classList.remove('hidden');
                });
                
                // Clear search input
                const searchInput = languageSelector.querySelector('.generic-search');
                if (searchInput) searchInput.value = '';
                
                // Hide dropdown
                const dropdown = languageSelector.querySelector('.generic-options');
                if (dropdown) dropdown.style.display = 'none';
            }
            
            // Clear generic learning languages selector
            const learningLanguageSelector = this.form.querySelector('#learning-languages-selector');
            if (learningLanguageSelector) {
                // Remove all selected learning languages
                const selectedLanguages = learningLanguageSelector.querySelectorAll('.selected-item-chip');
                selectedLanguages.forEach(element => element.remove());
                
                // Make all options visible again
                const languageOptions = learningLanguageSelector.querySelectorAll('.option-item');
                languageOptions.forEach(option => {
                    option.style.display = '';
                    option.classList.remove('hidden');
                });
                
                // Clear search input
                const searchInput = learningLanguageSelector.querySelector('.generic-search');
                if (searchInput) searchInput.value = '';
                
                // Hide dropdown
                const dropdown = learningLanguageSelector.querySelector('.generic-options');
                if (dropdown) dropdown.style.display = 'none';
            }
            
            // Uncheck Alpine-driven checkbox chips
            this.resetCheckboxChips('input[name="native_only"]');
            this.resetCheckboxChips('input[name="learning_known"]');
            this.resetCheckboxChips('input[name="lfor[]"]');
            this.resetCheckboxChips('input[name="continents[]"]');

            // Clear interests/tags — remove DOM inputs and reset Alpine state
            this.form.querySelectorAll('input[name="tags[]"]').forEach(el => el.remove());
            const interestsEl = this.form.querySelector('[x-data*="searchInterestsFilter"]');
            if (interestsEl && interestsEl._x_dataStack) {
                interestsEl._x_dataStack[0].selectedTags = [];
            }

            // Clear keywords
            const keywordsInput = this.form.querySelector('input[name="keywords"]');
            if (keywordsInput) keywordsInput.value = '';

            // Clear username
            const usernameInput = this.form.querySelector('input[name="username"]');
            if (usernameInput) usernameInput.value = '';
            
            // Submit the form to update results
            this.submitForm();
        });
    }
    
    /**
     * Handle clicking outside to close dropdowns
     */
    handleOutsideClick(event) {
        // Note: Sort dropdown outside-click is handled by Alpine.js (@click.outside)

        // Close all expandable chip dropdowns
        const expandableChips = document.querySelectorAll('.expandable-filter-chip');
        expandableChips.forEach(chip => {
            const container = chip.closest('.filter-chip-container');
            if (!container) return; // Skip if no container found

            const dropdown = container.querySelector('.filter-chip-dropdown');
            const chevron = chip.querySelector('.fa-chevron-down');

            if (dropdown && !container.contains(event.target) && getComputedStyle(dropdown).display !== 'none') {
                dropdown.style.display = 'none';
                chip.setAttribute('aria-expanded', 'false');
                if (chevron) chevron.classList.remove('rotate-180');
            }
        });

        // Close generic selector dropdowns
        const genericSelectors = document.querySelectorAll('.generic-selector');
        genericSelectors.forEach(selector => {
            const dropdown = selector.querySelector('.generic-options');
            const container = selector.closest('.generic-selector-container');

            if (dropdown && container && !container.contains(event.target) && getComputedStyle(dropdown).display !== 'none') {
                dropdown.style.display = 'none';
            }
        });
    }
    
    /**
     * Clear the Nearby quick chip's Alpine state and disable its hidden inputs
     */
    clearNearbyChip() {
        const nearbyEl = this.form.querySelector('[x-data*="nearbyChip"]');
        if (nearbyEl?._x_dataStack) {
            nearbyEl._x_dataStack[0].active = false;
            nearbyEl._x_dataStack[0].location = '';
        }
        // Disable hidden inputs so they don't submit
        nearbyEl?.querySelectorAll('input[name="location"], input[name="radius"]').forEach(el => {
            el.disabled = true;
        });
    }

    /**
     * Check if location-type filters (countries, city, continents) are active
     */
    hasLocationFilters() {
        const hasCountries = this.form.querySelectorAll('input[name="countries[]"]:not([disabled])').length > 0;
        const hasCity = !!this.form.querySelector('input[name="city"]')?.value;
        // Continent checkboxes use Alpine `on` state — check via :checked or Alpine stack
        let hasContinents = false;
        this.form.querySelectorAll('input[name="continents[]"]').forEach(cb => {
            if (cb.checked) hasContinents = true;
            const chip = cb.closest('.filter-checkbox-chip');
            if (chip?._x_dataStack?.[0]?.on) hasContinents = true;
        });
        return hasCountries || hasCity || hasContinents;
    }

    /**
     * Submit the search form
     */
    submitForm() {
        // Reset offset to show first page of results
        const offsetInput = this.form.querySelector('input[name="offset"]');
        if (offsetInput) offsetInput.value = '0';

        // Reset page to 1
        const pageInput = this.form.querySelector('input[name="page"]');
        if (pageInput) pageInput.value = '1';

        // Location filters override Nearby chip
        if (this.hasLocationFilters()) {
            this.clearNearbyChip();
        }

        // Show loading skeletons at results level during page navigation
        const viewMode = this.form.querySelector('input[name="viewMode"]')?.value || 'list';
        if (window.showSearchLoadingState) {
            window.showSearchLoadingState(viewMode);
        }

        // Submit the form
        this.form.submit();
    }
    
    /**
     * Initialize select elements (countries, languages, etc.)
     */
    initializeSelects() {
        const selectElements = [
            '.location-country',
            '.language-select',
            '.home-country-select'
        ];
        
        selectElements.forEach(selector => {
            const select = document.querySelector(selector);
            if (select) {
                
                // Check if using select2 or similar library
                if (typeof $ !== 'undefined' && typeof $.fn.select2 !== 'undefined') {
                    try {
                        $(selector).select2({
                            width: '100%',
                            placeholder: select.getAttribute('placeholder') || 'Select option(s)'
                        });
                    } catch (e) {
                    }
                }
            } else {
            }
        });
    }
    
    
    /**
     * Setup sort chips dropdown
     * Sort selection is now handled entirely by Alpine.js @click handlers in the template.
     * They update the hidden #sort-input and submit the form directly.
     * This method is kept for backwards compatibility but does nothing.
     */
    setupSortChips() {
        // Sort functionality is now handled by Alpine.js in filter-chips.twig
        // The @click handlers update #sort-input and submit the form
    }
    
    /**
     * Setup save filters chip
     */
    setupSaveFiltersChip() {
        const saveFiltersChip = document.querySelector('#save-filters-chip');
        if (!saveFiltersChip) {
            return;
        }
        
        saveFiltersChip.addEventListener('click', () => {
            
            // Use the existing save functionality
            if (typeof window.saveFilters === 'function') {
                window.saveFilters();
            } else {
                // If no saveFilters function exists, use the existing link
                const saveLink = document.querySelector('#sCSave');
                if (saveLink) {
                    saveLink.click();
                } else {
                    alert('Save filters functionality not available');
                }
            }
        });
    }

    /**
     * Setup range sliders
     */
    setupRangeSliders() {
        // jQuery UI sliders are initialized in the template
    }
    
    /**
     * Reset radius select to default value
     * @param {string} value - The value to set
     */
    resetRadiusSelect(value) {
        const radiusSelect = document.getElementById('radius_input');
        if (radiusSelect) radiusSelect.value = value;
    }

    /**
     * Setup interactive generic selector (languages, etc.)
     */
    setupGenericSelector() {
        const genericSelectors = document.querySelectorAll('.generic-selector-container');
        if (genericSelectors.length === 0) {
            return;
        }

        genericSelectors.forEach(container => {
            const id = container.id;
            const selector = container.querySelector('.generic-selector');
            const searchInput = selector.querySelector('.generic-search');
            const optionsContainer = selector.querySelector('.generic-options');
            const selectedItemsContainer = selector.querySelector('.selected-items-chips');

            if (!searchInput || !optionsContainer || !selectedItemsContainer) {
                return;
            }

            // Prevent change events from within this container from bubbling up to the form
            container.addEventListener('change', (e) => {
                e.stopPropagation();
            }, false);

            // Show options when clicking on search input
            searchInput.addEventListener('click', (e) => {
                e.stopPropagation();
                optionsContainer.style.display = 'block';
            });

            // Also show dropdown when focusing on the search input
            searchInput.addEventListener('focus', (e) => {
                e.stopPropagation();
                optionsContainer.style.display = 'block';
            });

            // Filter options as user types
            searchInput.addEventListener('input', () => {
                if (optionsContainer.style.display !== 'block') {
                    optionsContainer.style.display = 'block';
                }

                const searchValue = searchInput.value.toLowerCase();
                const optionItems = optionsContainer.querySelectorAll('.option-item');

                optionItems.forEach(item => {
                    if (item.style.display === 'none') {
                        return;
                    }

                    const optionName = item.getAttribute('data-option-name').toLowerCase();

                    if (optionName.includes(searchValue)) {
                        item.classList.remove('hidden');
                    } else {
                        item.classList.add('hidden');
                    }
                });
            });

            // Add option when clicked
            const optionItems = optionsContainer.querySelectorAll('.option-item');
            optionItems.forEach(item => {
                item.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const code = item.getAttribute('data-option-code');
                    const name = item.getAttribute('data-option-name');

                    let inputName = selectedItemsContainer.querySelector('input')?.name;
                    if (!inputName) {
                        if (id.includes('learning-languages')) {
                            inputName = 'learn[]';
                        } else if (id.includes('languages')) {
                            inputName = 'languages[]';
                        } else if (id.includes('country')) {
                            inputName = 'countries[]';
                        } else if (id.includes('home-country')) {
                            inputName = 'home_countries[]';
                        }
                    }

                    const isAlreadySelected = selectedItemsContainer.querySelector(`input[name="${inputName}"][value="${code}"]`);
                    if (isAlreadySelected) {
                        return;
                    }

                    const showFlags = id.includes('country');

                    const itemElement = document.createElement('div');
                    itemElement.className = 'selected-item-chip flex items-center gap-1 rounded-full px-3 py-1.5 text-sm bg-sky-100 border border-sky-300 text-sky-700';

                    let flagHtml = '';
                    if (showFlags && code.length === 2) {
                        flagHtml = `<span class="option-icon mr-1 inline-block">
                            <img src="/images/circle-flags/flags/${code.toLowerCase()}.svg" alt="${code}" class="w-5 h-5 rounded-full">
                        </span>`;
                    }

                    itemElement.innerHTML = `
                        ${flagHtml}
                        <span>${name}</span>
                        <button type="button" class="remove-item text-sky-500 hover:text-sky-700 ml-1 border-0 bg-transparent p-0 focus:outline-none" data-option-code="${code}">
                            <i class="fas fa-times"></i>
                        </button>
                    `;

                    itemElement.innerHTML += `<input type="hidden" name="${inputName}" value="${code}">`;

                    selectedItemsContainer.appendChild(itemElement);

                    item.style.display = 'none';

                    searchInput.value = '';

                    optionsContainer.querySelectorAll('.option-item').forEach(option => {
                        if (option.style.display !== 'none') {
                            option.classList.remove('hidden');
                        }
                    });

                    const removeButton = itemElement.querySelector('.remove-item');
                    this.setupRemoveItemButton(removeButton);
                });
            });

            // Make sure clicks in the dropdown don't close it
            optionsContainer.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            // Setup existing remove buttons
            const removeButtons = selectedItemsContainer.querySelectorAll('.remove-item');
            removeButtons.forEach(button => {
                this.setupRemoveItemButton(button);
            });
        });
    }

    /**
     * Setup remove button for generic selector items
     */
    setupRemoveItemButton(button) {
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();

            const code = button.getAttribute('data-option-code');
            const itemElement = button.closest('.selected-item-chip');
            const selectorContainer = itemElement.closest('.generic-selector');
            const optionsContainer = selectorContainer.querySelector('.generic-options');

            itemElement.remove();

            const optionItem = optionsContainer.querySelector(`.option-item[data-option-code="${code}"]`);
            if (optionItem) {
                optionItem.style.display = '';
                optionItem.classList.remove('hidden');
            }

            optionsContainer.style.display = 'block';
        });
    }

}

// Make the class available globally
window.SearchFilters = SearchFilters;